import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { authApi, usd } from "@/lib/auth";
import { AdminHeader } from "@/pages/admin/Dashboard";

const EMPTY = {
  supplier_name: "", product: "", model_number: "", wholesale_cost: "", freight_estimate: "",
  install_estimate: "", lead_time_days: "", warranty: "", specs: "",
};

const inputCls =
  "w-full bg-white/[0.04] border hairline rounded-sm px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#A7D7A0]/60";
const labelCls = "block text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/40 mb-1.5";

export default function Suppliers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null); // null = closed, object = editing/adding
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authApi.me().then(setUser).catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);

  const load = () => authApi.suppliers().then((d) => setItems(d.suppliers)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  const openNew = () => setForm({ ...EMPTY, supplier_id: null });
  const openEdit = (s) =>
    setForm({
      ...s, supplier_id: s.supplier_id,
      wholesale_cost: String(s.wholesale_cost), freight_estimate: String(s.freight_estimate),
      install_estimate: String(s.install_estimate), lead_time_days: String(s.lead_time_days),
    });

  const save = async () => {
    if (!form.supplier_name.trim() || !form.product.trim()) {
      toast.error("Supplier name and product are required");
      return;
    }
    setSaving(true);
    const payload = {
      supplier_name: form.supplier_name.trim(),
      product: form.product.trim(),
      model_number: form.model_number.trim(),
      wholesale_cost: Number(form.wholesale_cost) || 0,
      freight_estimate: Number(form.freight_estimate) || 0,
      install_estimate: Number(form.install_estimate) || 0,
      lead_time_days: parseInt(form.lead_time_days) || 0,
      warranty: form.warranty.trim(),
      specs: form.specs.trim(),
    };
    try {
      if (form.supplier_id) await authApi.updateSupplier(form.supplier_id, payload);
      else await authApi.createSupplier(payload);
      toast.success(form.supplier_id ? "Product updated" : "Product added");
      setForm(null);
      load();
    } catch {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.product}?`)) return;
    try {
      await authApi.deleteSupplier(s.supplier_id);
      load();
    } catch {
      toast.error("Could not delete product");
    }
  };

  if (!user) return <main className="min-h-screen bg-void" />;

  return (
    <main className="min-h-screen bg-void" data-testid="admin-suppliers">
      <AdminHeader user={user} />
      <div className="px-4 sm:px-8 max-w-7xl mx-auto py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">Supplier Pricing</h1>
            <p className="text-muted-green text-sm mt-1">Wholesale costs used to build quotes. Never shown to customers.</p>
          </div>
          <button data-testid="supplier-add-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-white transition-colors">
            <Plus size={15} /> Add Product
          </button>
        </div>

        {form && (
          <div className="bg-card-dark border hairline rounded-md p-7 mb-8" data-testid="supplier-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-semibold text-lg">{form.supplier_id ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => setForm(null)} className="text-white/40 hover:text-white" data-testid="supplier-form-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div><label className={labelCls}>Supplier Name</label><input data-testid="sup-name" className={inputCls} value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="e.g. Musco, Eaton" /></div>
              <div><label className={labelCls}>Lighting Product</label><input data-testid="sup-product" className={inputCls} value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g. LED Sports Floodlight 600W" /></div>
              <div><label className={labelCls}>Model Number</label><input data-testid="sup-model" className={inputCls} value={form.model_number} onChange={(e) => setForm({ ...form, model_number: e.target.value })} /></div>
              <div><label className={labelCls}>Lead Time (days)</label><input data-testid="sup-leadtime" type="number" min="0" className={inputCls} value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} /></div>
              <div><label className={labelCls}>Wholesale Cost ($)</label><input data-testid="sup-wholesale" type="number" min="0" step="0.01" className={inputCls} value={form.wholesale_cost} onChange={(e) => setForm({ ...form, wholesale_cost: e.target.value })} /></div>
              <div><label className={labelCls}>Freight Estimate ($)</label><input data-testid="sup-freight" type="number" min="0" step="0.01" className={inputCls} value={form.freight_estimate} onChange={(e) => setForm({ ...form, freight_estimate: e.target.value })} /></div>
              <div><label className={labelCls}>Install Estimate ($)</label><input data-testid="sup-install" type="number" min="0" step="0.01" className={inputCls} value={form.install_estimate} onChange={(e) => setForm({ ...form, install_estimate: e.target.value })} /></div>
              <div><label className={labelCls}>Warranty</label><input data-testid="sup-warranty" className={inputCls} value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} placeholder="e.g. 10-year parts" /></div>
              <div className="sm:col-span-2 lg:col-span-4"><label className={labelCls}>Technical Specifications</label><textarea data-testid="sup-specs" rows={2} className={inputCls} value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} placeholder="Lumens, color temp, voltage, IP rating…" /></div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setForm(null)} className="text-sm text-white/50 hover:text-white px-4 py-2.5" data-testid="supplier-cancel-btn">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="supplier-save-btn" className="bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-6 py-2.5 rounded-sm hover:bg-white transition-colors disabled:opacity-50">
                {saving ? "Saving…" : "Save Product"}
              </button>
            </div>
          </div>
        )}

        <div className="border hairline rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b hairline text-left">
                  {["Supplier", "Product / Model", "Wholesale", "Freight", "Install", "Unit Direct", "Lead Time", ""].map((h, i) => (
                    <th key={i} className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.supplier_id} className="border-b hairline last:border-0 hover:bg-white/[0.02]" data-testid={`supplier-row-${s.supplier_id}`}>
                    <td className="px-5 py-4 text-white font-medium">{s.supplier_name}</td>
                    <td className="px-5 py-4 text-white/70">{s.product}{s.model_number ? <span className="text-white/35 text-xs block">{s.model_number}</span> : null}</td>
                    <td className="px-5 py-4 text-white/70">{usd(s.wholesale_cost)}</td>
                    <td className="px-5 py-4 text-white/70">{usd(s.freight_estimate)}</td>
                    <td className="px-5 py-4 text-white/70">{usd(s.install_estimate)}</td>
                    <td className="px-5 py-4 text-[#A7D7A0] font-medium">{usd(s.wholesale_cost + s.freight_estimate + s.install_estimate)}</td>
                    <td className="px-5 py-4 text-white/50">{s.lead_time_days}d</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.supplier_id}`} className="text-white/40 hover:text-white p-1.5"><Pencil size={14} /></button>
                      <button onClick={() => remove(s)} data-testid={`supplier-delete-${s.supplier_id}`} className="text-white/40 hover:text-red-400 p-1.5"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-14 text-center text-white/30 text-sm" data-testid="suppliers-empty">No supplier products yet. Add your first product to start drafting quotes.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
}
